import React, { useState, useEffect, useContext, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import { UserContext } from "../../../App";
import "./ProfilePage.css";
import { AVAILABLE_AVATARS, avatarImages } from "./AvatarConfig";
import {
  AVAILABLE_BACKGROUNDS,
  backgroundImages,
  backgroundNames,
} from "./BackgroundConfig";
import DeleteAccountModal from "./DeleteAccountModal";
import { useNavigate } from "react-router-dom";
import LogoutButton from "../../LogoutButton";
import { useTraining } from "../../../contexts/TrainingContext";
import { useTrainingTips } from "../../../hooks/useTrainingTips";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

const formatDate = (dateField) => {
  if (!dateField) return "N/A";

  // Handle string dates (from Firebase Auth lastSignInTime)
  if (typeof dateField === "string") {
    return new Date(dateField).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  // Handle Firestore Timestamp
  if (dateField && dateField.seconds) {
    return new Date(dateField.seconds * 1000).toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  if (dateField instanceof Date) {
    return dateField.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  return "N/A";
};

const ProfilePage = () => {
  const { loading, setUserData, ...userData } = useContext(UserContext);
  const [localLoading, setLocalLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [success, setSuccess] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  // Training system
  const { isTrainingEnabled, toggleTraining, currentTip } = useTraining();
  const {
    showTipForComponent,
    showNextPageTip,
    getTrainingIndicatorClass,
    addTrainingIndicator,
    removeTrainingIndicator,
  } = useTrainingTips("profile");

  // Refs for training tips
  const trainingToggleRef = useRef(null);
  const avatarSectionRef = useRef(null);
  const backgroundSectionRef = useRef(null);
  const passwordSectionRef = useRef(null);
  const saveButtonRef = useRef(null);
  const hasShownInitialTip = useRef(false);

  const [formData, setFormData] = useState({
    avatar: userData?.avatar || "Avatar0",
    background: userData?.background || "blob",
  });

  const [previewBackground, setPreviewBackground] = useState(null);

  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (
      userData?.avatar !== formData.avatar ||
      userData?.background !== formData.background
    ) {
      setFormData({
        avatar: userData?.avatar || "Avatar0",
        background: userData?.background || "blob",
      });
      // Reset preview when userData changes
      setPreviewBackground(null);
    }
  }, [userData?.avatar, userData?.background]);

  useEffect(() => {
    let timeoutId;
    if (success) {
      timeoutId = setTimeout(() => {
        setSuccess("");
      }, 5000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [success]);

  useEffect(() => {
    let timeoutId;
    if (passwordSuccess) {
      timeoutId = setTimeout(() => {
        setPasswordSuccess("");
        setShowPasswordForm(false);
      }, 5000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [passwordSuccess]);

  // Show training toggle tip when training mode is enabled
  useEffect(() => {
    if (loading || hasShownInitialTip.current) return;

    if (isTrainingEnabled) {
      hasShownInitialTip.current = true;

      // Check if the training toggle tip has already been dismissed
      const trainingToggleTipDismissed = localStorage
        .getItem("socrates_dismissed_tips")
        ?.includes("profile_training_toggle");

      if (!trainingToggleTipDismissed && trainingToggleRef.current) {
        // Show training toggle tip
        showTipForComponent(
          "profile_training_toggle",
          trainingToggleRef.current,
          "bottom",
          1000
        );
      }
    }
  }, [loading, isTrainingEnabled, showTipForComponent]);

  // Reset flags when training is disabled
  useEffect(() => {
    if (!isTrainingEnabled) {
      hasShownInitialTip.current = false;
    }
  }, [isTrainingEnabled]);

  // Function to show tips for sections when they're hovered
  const showSectionTip = (tipId, ref, position = "right") => {
    if (ref.current) {
      showTipForComponent(tipId, ref.current, position, 200);
    }
  };

  // Manage training indicators when training mode changes
  useEffect(() => {
    const refs = [
      { ref: avatarSectionRef, tipId: "profile_avatar", type: "form-section" },
      {
        ref: backgroundSectionRef,
        tipId: "profile_background",
        type: "form-section",
      },
      { ref: passwordSectionRef, tipId: "profile_password", type: "section" },
      { ref: saveButtonRef, tipId: "profile_save", type: "button" },
    ];

    refs.forEach(({ ref, tipId, type }) => {
      if (ref.current) {
        if (isTrainingEnabled) {
          addTrainingIndicator(ref.current, tipId, type);
        } else {
          removeTrainingIndicator(ref.current);
        }
      }
    });

    // Cleanup function to remove indicators when component unmounts
    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) {
          removeTrainingIndicator(ref.current);
        }
      });
    };
  }, [isTrainingEnabled, addTrainingIndicator, removeTrainingIndicator]);

  // Apply preview background
  useEffect(() => {
    if (previewBackground) {
      const backgroundImage = backgroundImages[previewBackground];
      document.body.style.backgroundImage = `url(${backgroundImage})`;
    }

    // Cleanup function to restore original background when component unmounts or preview changes
    return () => {
      if (previewBackground) {
        // Restore user's actual background or default
        const currentBackground = userData?.background || "blob";
        const currentBackgroundImage = backgroundImages[currentBackground];
        document.body.style.backgroundImage = `url(${currentBackgroundImage})`;
      }
    };
  }, [previewBackground, userData?.background]);

  const handleAvatarSelect = (avatar) => {
    setFormData((prev) => ({
      ...prev,
      avatar,
    }));
  };

  const handleBackgroundSelect = (background) => {
    setFormData((prev) => ({
      ...prev,
      background,
    }));
    // Set preview background immediately
    setPreviewBackground(background);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordForm = () => {
    setShowPasswordForm(!showPasswordForm);
    if (!showPasswordForm) {
      setPasswordData({
        newPassword: "",
        confirmPassword: "",
      });
      setPasswordError("");
      setPasswordSuccess("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalLoading(true);
    setError("");
    setSuccess("");

    try {
      const updateProfile = httpsCallable(functions, "updateProfile");
      await updateProfile({
        avatar: formData.avatar,
        background: formData.background,
      });

      setUserData((prevData) => ({
        ...prevData,
        avatar: formData.avatar,
        background: formData.background,
      }));

      // Clear preview since the background is now saved
      setPreviewBackground(null);
      setSuccess("Profile updated successfully");
    } catch (error) {
      setError(error.message);
    } finally {
      setLocalLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("Passwords do not match");
      setPasswordLoading(false);
      return;
    }

    try {
      const changePassword = httpsCallable(functions, "changePassword");
      await changePassword({
        newPassword: passwordData.newPassword,
      });

      setPasswordSuccess("Password updated successfully");
      setPasswordData({
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      setPasswordError(error.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleRequestDeletion = async () => {
    setDeleteLoading(true);
    try {
      const requestDeletion = httpsCallable(
        functions,
        "requestAccountDeletion"
      );
      await requestDeletion();
      setSuccess(
        "Your account deletion request has been submitted. An administrator will review your request."
      );
      setShowDeleteModal(false);
    } catch (error) {
      setError(error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return <div className="profile-page">Loading profile...</div>;
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-info">
          <img
            src={avatarImages[formData.avatar]}
            alt="Current Avatar"
            className="current-avatar"
          />
          <div className="user-details">
            <h1>{`${userData.firstName || ""} ${userData.lastName || ""}`}</h1>
            <p className="user-role">{userData.role || "Student"}</p>
            <p className="user-email">
              {userData.email || "No email provided"}
            </p>
            <div className="user-dates">
              <p>Registered: {formatDate(userData.registered)}</p>
              <p>Last Login: {formatDate(userData.lastLogin)}</p>
            </div>

            <div
              className="profile-actions"
              ref={passwordSectionRef}
              onMouseEnter={() =>
                showSectionTip("profile_password", passwordSectionRef, "left")
              }
            >
              <div className="password-section">
                {!showPasswordForm ? (
                  <div className="profile-buttons">
                    <button
                      type="button"
                      className="change-password-btn"
                      onClick={togglePasswordForm}
                    >
                      Change Password
                    </button>
                    <LogoutButton />
                  </div>
                ) : (
                  <form
                    onSubmit={handlePasswordSubmit}
                    className="password-form"
                  >
                    <div className="form-group">
                      <input
                        type="password"
                        id="newPassword"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        placeholder="New Password"
                        required
                        minLength="6"
                      />
                    </div>

                    <div className="form-group">
                      <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        placeholder="Confirm Password"
                        required
                        minLength="6"
                      />
                    </div>

                    <div className="password-form-buttons">
                      <button
                        type="submit"
                        disabled={passwordLoading}
                        className="update-password-btn"
                      >
                        {passwordLoading ? "Updating..." : "Update Password"}
                      </button>

                      <button
                        type="button"
                        className="cancel-password-btn"
                        onClick={togglePasswordForm}
                        disabled={passwordLoading}
                      >
                        Cancel
                      </button>
                    </div>

                    {passwordError && (
                      <div className="error-message">{passwordError}</div>
                    )}
                    {passwordSuccess && (
                      <div className="success-message">{passwordSuccess}</div>
                    )}
                  </form>
                )}
              </div>

              <button
                type="button"
                className="delete-account-btn danger"
                onClick={() => setShowDeleteModal(true)}
              >
                Request Account Deletion
              </button>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        {/* Training Mode Toggle */}
        <div className="form-section training-section">
          <div className="training-header">
            <h2>
              <FontAwesomeIcon
                icon={faQuestionCircle}
                className="training-icon"
              />
              Training Mode
            </h2>
            <p className="training-description">
              Enable helpful tips and guidance throughout Socrates to learn how
              to use the platform effectively.
            </p>
          </div>
          <div className="training-toggle-container" ref={trainingToggleRef}>
            <label className="training-toggle">
              <input
                type="checkbox"
                checked={isTrainingEnabled}
                onChange={toggleTraining}
                className="training-checkbox"
              />
              <span className="training-slider"></span>
            </label>
            <span className="training-label">
              {isTrainingEnabled
                ? "Training Mode Enabled"
                : "Training Mode Disabled"}
            </span>
          </div>
        </div>

        <div
          className="form-section"
          ref={avatarSectionRef}
          onMouseEnter={() =>
            showSectionTip("profile_avatar", avatarSectionRef)
          }
        >
          <h2>Avatar Selection</h2>
          <div className="avatar-grid">
            {AVAILABLE_AVATARS.map((avatar) => (
              <div
                key={avatar}
                className={`avatar-option ${
                  formData.avatar === avatar ? "selected" : ""
                }`}
                onClick={() => handleAvatarSelect(avatar)}
              >
                <img
                  src={avatarImages[avatar]}
                  alt={`Avatar option ${avatar}`}
                  className="avatar-image"
                />
              </div>
            ))}
          </div>
        </div>

        <div
          className="form-section"
          ref={backgroundSectionRef}
          onMouseEnter={() =>
            showSectionTip("profile_background", backgroundSectionRef)
          }
        >
          <h2>Background Theme Selection</h2>
          <div className="background-grid">
            {AVAILABLE_BACKGROUNDS.map((background) => (
              <div
                key={background}
                className={`background-option ${
                  formData.background === background ? "selected" : ""
                }`}
                onClick={() => handleBackgroundSelect(background)}
              >
                <div
                  className="background-preview"
                  style={{
                    backgroundImage: `url(${backgroundImages[background]})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                />
                <span className="background-name">
                  {backgroundNames[background]}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <button
            ref={saveButtonRef}
            type="submit"
            disabled={localLoading}
            className="update-profile-btn"
            onMouseEnter={() =>
              showSectionTip("profile_save", saveButtonRef, "top")
            }
          >
            {localLoading ? "Updating..." : "Update Profile"}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
      </form>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleRequestDeletion}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default ProfilePage;
